import { useState } from 'react';
import { studentsAPI } from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import {
  isStudentDirectoryRow,
  parseHighestDegreeYear,
  readHighestDegreeYear,
  resolveHighestDegree,
} from '../../domain/filters/highestDegreeYear';

/**
 * Inline editor for highest-degree yearOfPassout (directory specific-detail column).
 */
const HighestDegreeYearCell = ({ student, canEdit, onPatched }) => {
  const current = readHighestDegreeYear(student);
  const highest = resolveHighestDegree(student);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(current == null ? '' : String(current));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!isStudentDirectoryRow(student)) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  if (!highest) {
    return (
      <span className="text-xs text-slate-500" title="Add a highest degree before setting year of passout">
        —
      </span>
    );
  }

  const startEdit = () => {
    if (!canEdit) return;
    setDraft(current == null ? '' : String(current));
    setError('');
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError('');
  };

  const save = async () => {
    const parsed = parseHighestDegreeYear(draft);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await studentsAPI.updateHighestDegreeYear(student.id, {
        yearOfPassout: parsed.year,
      });
      onPatched?.(student.id, {
        academicQualifications: res.data?.academicQualifications || student.academicQualifications,
      });
      setEditing(false);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not update year'));
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return <span className="text-sm">{current ?? '—'}</span>;
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={startEdit}
        className="text-sm text-left px-1 py-0.5 rounded hover:bg-slate-100"
        title="Edit highest degree year of passout"
      >
        {current ?? <span className="text-slate-400">Set year</span>}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1 min-w-[7rem]" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1">
        <input
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="w-20 px-2 py-1 border rounded text-sm"
          min={1950}
          max={2100}
          disabled={saving}
          aria-label="Highest degree year of passout"
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="text-xs text-white bg-blue-600 px-2 py-1 rounded disabled:opacity-50"
        >
          {saving ? '…' : 'Save'}
        </button>
        <button type="button" onClick={cancel} className="text-xs text-slate-600 px-1">
          Cancel
        </button>
      </div>
      {error ? <p className="text-xs text-red-700">{error}</p> : null}
    </div>
  );
};

export default HighestDegreeYearCell;
